// ══════════════════════════════════════════════════════════════════════════════
//   REGISTRATION VALIDATION — shared by the website and the Supabase Edge Functions.
//   The form uses it for instant feedback; the `register` function runs it again on the
//   server, because anything the browser sends can be faked.
//
//   Keep this file self-contained (no imports): `npm run functions:sync` copies it into
//   supabase/functions/_shared/, where it has to work in Deno as well.
// ══════════════════════════════════════════════════════════════════════════════

export const LIMITS = {
  name: 80,
  email: 254,
  phone: 30,
  organisation: 160,
  professionalRole: 120,
};

export interface RegistrationInput {
  webinarId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  organisation?: string;
  professionalRole?: string;
  privacyNoticeAccepted: boolean;
  marketingConsent: boolean;
}

export type RegistrationField =
  | "webinarId" | "firstName" | "lastName" | "email" | "phone" | "organisation" | "professionalRole" | "privacyNoticeAccepted";

export type RegistrationErrors = Partial<Record<RegistrationField, string>>;

export type ValidationResult =
  | { ok: true; value: RegistrationInput }
  | { ok: false; errors: RegistrationErrors };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Duplicate checks compare addresses in this form, so "Thandi@Example.com " matches "thandi@example.com" */
export const normaliseEmail = (email: string) => email.trim().toLowerCase();

export const isUuid = (value: string) => UUID_PATTERN.test(value);

const text = (value: unknown) => (typeof value === "string" ? value.trim() : "");

/**
 * Checks a registration and returns it cleaned up (trimmed, email normalised, empty optional
 * fields removed), or the problems keyed by field. Accepts untrusted input of any shape.
 */
export function validateRegistration(raw: unknown): ValidationResult {
  const input = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const errors: RegistrationErrors = {};

  const webinarId = text(input.webinarId);
  const firstName = text(input.firstName);
  const lastName = text(input.lastName);
  const email = normaliseEmail(text(input.email));
  const phone = text(input.phone);
  const organisation = text(input.organisation);
  const professionalRole = text(input.professionalRole);

  if (!webinarId || webinarId.length > 64) errors.webinarId = "This webinar could not be found.";

  if (!firstName) errors.firstName = "Enter your first name.";
  else if (firstName.length > LIMITS.name || CONTROL_CHARACTERS.test(firstName)) errors.firstName = `Use up to ${LIMITS.name} characters.`;

  if (!lastName) errors.lastName = "Enter your surname.";
  else if (lastName.length > LIMITS.name || CONTROL_CHARACTERS.test(lastName)) errors.lastName = `Use up to ${LIMITS.name} characters.`;

  if (!EMAIL_PATTERN.test(email) || email.length > LIMITS.email || CONTROL_CHARACTERS.test(email)) {
    errors.email = "Enter a valid email address.";
  }

  const digits = phone.replace(/\D/g, "");
  if (digits.length < 9 || digits.length > 15 || phone.length > LIMITS.phone || /[^\d\s()+-]/.test(phone)) {
    errors.phone = "Enter a valid phone number, e.g. 082 123 4567.";
  }

  if (organisation.length > LIMITS.organisation || CONTROL_CHARACTERS.test(organisation)) {
    errors.organisation = `Use up to ${LIMITS.organisation} characters.`;
  }
  if (professionalRole.length > LIMITS.professionalRole || CONTROL_CHARACTERS.test(professionalRole)) {
    errors.professionalRole = `Use up to ${LIMITS.professionalRole} characters.`;
  }

  if (input.privacyNoticeAccepted !== true) errors.privacyNoticeAccepted = "Please confirm you have read the privacy notice.";

  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return {
    ok: true,
    value: {
      webinarId,
      firstName,
      lastName,
      email,
      phone,
      organisation: organisation || undefined,
      professionalRole: professionalRole || undefined,
      privacyNoticeAccepted: true,
      marketingConsent: input.marketingConsent === true,
    },
  };
}
